import { useEffect, useState } from "react";
import { Mail, Save, Send, BarChart3, Plus, Trash2, Edit2, Server, Key, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CompactStatCard, PageHeader, StatsGrid } from "@/components/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useEmailSettings,
  useEmailStats,
  useSendTestEmailMutation,
  useUpdateEmailSettingsMutation,
} from "@/hooks/queries";
import type { EmailSettingsDto, EmailProviderSettingsDto } from "@/types/integration.dto";
import { EmailApiProvider, EmailProviderType } from "@/types/integration.dto";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

const apiProviderOptions = [
  { label: "SendGrid", value: String(EmailApiProvider.SendGrid) },
  { label: "Mailgun", value: String(EmailApiProvider.Mailgun) },
  { label: "Resend", value: String(EmailApiProvider.Resend) },
  { label: "Custom", value: String(EmailApiProvider.Custom) },
];

const EmailSettingsSection = () => {
  const { toast } = useToast();
  const { data, isLoading } = useEmailSettings();
  const { data: stats, isLoading: statsLoading } = useEmailStats();
  const updateMutation = useUpdateEmailSettingsMutation();
  const testMutation = useSendTestEmailMutation();

  const [settings, setSettings] = useState<EmailSettingsDto | null>(null);
  const [testEmail, setTestEmail] = useState("");

  // Modal Dialog State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<EmailProviderSettingsDto>>({});

  useEffect(() => {
    if (data) setSettings(data);
  }, [data]);

  if (isLoading || !settings) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        enabled: settings.enabled,
        dailySendLimit: settings.dailySendLimit,
        totalSendLimit: settings.totalSendLimit,
        providers: settings.providers,
      });
      toast({ title: "Email settings saved" });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const openAddModal = () => {
    setFormData({
      id: crypto.randomUUID(),
      name: "",
      providerType: EmailProviderType.Smtp,
      fromEmail: "",
      fromName: "",
      replyToEmail: "",
      smtpHost: "",
      smtpPort: 587,
      smtpUseSsl: true,
      smtpUsername: "",
      smtpPassword: "",
      apiProvider: EmailApiProvider.SendGrid,
      apiKey: "",
      apiBaseUrl: "",
      isActive: (settings.providers || []).length === 0,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (provider: EmailProviderSettingsDto) => {
    setFormData({ ...provider, smtpPassword: "", apiKey: "" });
    setIsModalOpen(true);
  };

  const saveProvider = () => {
    if (!formData.name?.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    if (!formData.fromEmail?.trim()) {
      toast({ title: "From Email is required", variant: "destructive" });
      return;
    }

    const list = [...(settings.providers || [])];
    const idx = list.findIndex((p) => p.id === formData.id);
    const newProvider = { ...formData } as EmailProviderSettingsDto;

    if (newProvider.isActive) {
      list.forEach((p) => (p.isActive = false));
    }

    if (idx >= 0) {
      list[idx] = newProvider;
    } else {
      list.push(newProvider);
    }

    const active = list.find((p) => p.isActive);
    if (!active && list.length > 0) {
      list[0].isActive = true;
    }

    setSettings({ ...settings, providers: list });
    setIsModalOpen(false);
  };

  const deleteProvider = (id: string) => {
    const list = (settings.providers || []).filter((p) => p.id !== id);
    const active = list.find((p) => p.isActive);
    if (!active && list.length > 0) {
      list[0].isActive = true;
    }
    setSettings({ ...settings, providers: list });
  };

  const toggleActiveProvider = (id: string) => {
    const list = (settings.providers || []).map((p) => ({
      ...p,
      isActive: p.id === id,
    }));
    setSettings({ ...settings, providers: list });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Email Settings"
        description="Configure multiple SMTP or API-based email providers. Only one active provider will deliver mail."
        actions={
          <Button onClick={openAddModal} size="sm" className="gap-1">
            <Plus className="h-4 w-4" /> Add Provider
          </Button>
        }
      />

      <StatsGrid columns={4}>
        <CompactStatCard title="Sent Today" value={statsLoading ? "…" : stats?.sentToday ?? 0} icon={Mail} />
        <CompactStatCard title="Sent Total" value={statsLoading ? "…" : stats?.sentTotal ?? 0} icon={BarChart3} />
        <CompactStatCard title="Remaining Today" value={statsLoading ? "…" : stats?.remainingToday ?? 0} icon={Send} />
        <CompactStatCard title="Remaining Total" value={statsLoading ? "…" : stats?.remainingTotal ?? 0} icon={BarChart3} />
      </StatsGrid>

      {stats && (
        <p className="text-xs text-muted-foreground">
          Daily limit: {stats.dailySendLimit} · Total limit: {stats.totalSendLimit}
          {stats.failedToday > 0 && ` · Failed today: ${stats.failedToday}`}
          {stats.lastSentAt && ` · Last sent: ${new Date(stats.lastSentAt).toLocaleString("en-IN")}`}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="overflow-hidden">
          <CardHeader className="p-3 pb-1.5">
            <CardTitle className="text-sm">Outbound</CardTitle>
            <CardDescription className="text-xs">Enable sending and volume limits</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 p-3 pt-0">
            <div className="flex items-center gap-2">
              <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} />
              <Label className="text-sm">Enable outbound email</Label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Daily limit</Label>
                <Input
                  type="number"
                  value={settings.dailySendLimit}
                  onChange={(e) => setSettings({ ...settings, dailySendLimit: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Total limit</Label>
                <Input
                  type="number"
                  value={settings.totalSendLimit}
                  onChange={(e) => setSettings({ ...settings, totalSendLimit: Number(e.target.value) })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden sm:col-span-1 lg:col-span-2">
          <CardHeader className="p-3 pb-1.5">
            <CardTitle className="text-sm">Send Test Email</CardTitle>
            <CardDescription className="text-xs">Uses the currently active provider</CardDescription>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="space-y-1.5 flex-1 min-w-[220px]">
                <Label className="text-xs">Test recipient</Label>
                <Input
                  placeholder="email@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
              </div>
              <Button
                variant="outline"
                disabled={!testEmail || testMutation.isPending}
                onClick={async () => {
                  try {
                    await testMutation.mutateAsync({ toEmail: testEmail });
                    toast({ title: "Test email sent successfully via active provider" });
                  } catch (error) {
                    toast({ title: getApiErrorMessage(error), variant: "destructive" });
                  }
                }}
              >
                <Send className="h-4 w-4 mr-2" /> Send Test
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold">Delivery Providers</h2>
            <p className="text-xs text-muted-foreground">
              Manage SMTP/API providers. Only one can be active.
            </p>
          </div>
          <Button onClick={openAddModal} size="sm" variant="outline" className="gap-1 lg:hidden">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>

        {(!settings.providers || settings.providers.length === 0) ? (
          <div className="glass-card flex flex-wrap items-center gap-3 rounded-xl border border-border p-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Mail className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">No providers added yet</p>
              <p className="text-xs text-muted-foreground">Add an SMTP or API delivery provider to start sending mail.</p>
            </div>
            <Button size="sm" className="gap-1" onClick={openAddModal}>
              <Plus className="h-4 w-4" /> Add Provider
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {settings.providers.map((p) => (
              <div
                key={p.id}
                className={`glass-card flex flex-wrap items-center gap-3 rounded-xl border p-3 ${
                  p.isActive ? "border-primary/60" : "border-border"
                }`}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  {p.providerType === EmailProviderType.Smtp ? <Server className="h-4 w-4" /> : <Key className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1 basis-48">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-sm font-semibold">{p.name}</p>
                    {p.isActive && (
                      <Badge
                        className="gap-1 border-emerald-500/20 bg-emerald-500/10 px-1.5 py-0 text-[10px] text-emerald-600"
                        variant="outline"
                      >
                        <Check className="h-3 w-3" /> Active
                      </Badge>
                    )}
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {p.fromName} · {p.fromEmail}
                  </p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {p.providerType === EmailProviderType.Smtp
                      ? `SMTP · ${p.smtpHost}:${p.smtpPort}`
                      : `API · ${apiProviderOptions.find((o) => o.value === String(p.apiProvider))?.label || "Custom"}`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {!p.isActive && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2.5 text-xs"
                      onClick={() => toggleActiveProvider(p.id)}
                    >
                      Activate
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    title="Edit provider"
                    onClick={() => openEditModal(p)}
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    title="Delete provider"
                    onClick={() => setDeleteId(p.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={handleSave} disabled={updateMutation.isPending} className="gap-2">
          <Save className="h-4 w-4" /> Save Email Settings
        </Button>
      </div>

      <Sheet open={isModalOpen} onOpenChange={setIsModalOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-2xl">
          <SheetHeader className="border-b px-6 py-4 text-left">
            <SheetTitle>
              {settings.providers?.some((p) => p.id === formData.id) ? "Edit Provider" : "Add Provider"}
            </SheetTitle>
            <SheetDescription>
              Configure credentials and sender identity for this delivery provider.
            </SheetDescription>
          </SheetHeader>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Provider Name</Label>
                <Input
                  placeholder="e.g., SendGrid Production"
                  value={formData.name || ""}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>From Name</Label>
                <Input
                  placeholder="e.g., Support Team"
                  value={formData.fromName || ""}
                  onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>From Email</Label>
                <Input
                  type="email"
                  placeholder="e.g., support@domain.com"
                  value={formData.fromEmail || ""}
                  onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Reply-To Email (optional)</Label>
                <Input
                  type="email"
                  placeholder="e.g., info@domain.com"
                  value={formData.replyToEmail || ""}
                  onChange={(e) => setFormData({ ...formData, replyToEmail: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Delivery Mode</Label>
                <Select
                  value={String(formData.providerType ?? EmailProviderType.Smtp)}
                  onValueChange={(v) =>
                    setFormData({ ...formData, providerType: Number(v) as EmailProviderType })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(EmailProviderType.Smtp)}>SMTP</SelectItem>
                    <SelectItem value={String(EmailProviderType.Api)}>API Key</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.providerType === EmailProviderType.Smtp ? (
              <div className="space-y-4 rounded-lg border bg-muted/20 p-4">
                <p className="text-sm font-medium">SMTP credentials</p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2 sm:col-span-2">
                    <Label>SMTP Host</Label>
                    <Input
                      placeholder="smtp.mailtrap.io"
                      value={formData.smtpHost || ""}
                      onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Port</Label>
                    <Input
                      type="number"
                      value={formData.smtpPort ?? 587}
                      onChange={(e) => setFormData({ ...formData, smtpPort: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      placeholder="SMTP Username"
                      value={formData.smtpUsername || ""}
                      onChange={(e) => setFormData({ ...formData, smtpUsername: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>
                      {formData.smtpPasswordConfigured ? "Password (leave blank to keep)" : "Password"}
                    </Label>
                    <Input
                      type="password"
                      placeholder="SMTP Password"
                      value={formData.smtpPassword || ""}
                      onChange={(e) => setFormData({ ...formData, smtpPassword: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.smtpUseSsl ?? true}
                    onCheckedChange={(v) => setFormData({ ...formData, smtpUseSsl: v })}
                  />
                  <Label>Use SSL/TLS Encryption</Label>
                </div>
              </div>
            ) : (
              <div className="space-y-4 rounded-lg border bg-muted/20 p-4">
                <p className="text-sm font-medium">API credentials</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>API Provider</Label>
                    <Select
                      value={String(formData.apiProvider ?? EmailApiProvider.SendGrid)}
                      onValueChange={(v) =>
                        setFormData({ ...formData, apiProvider: Number(v) as EmailApiProvider })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {apiProviderOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>
                      {formData.apiKeyConfigured ? "API Key (leave blank to keep)" : "API Key"}
                    </Label>
                    <Input
                      type="password"
                      placeholder="API Access Token"
                      value={formData.apiKey || ""}
                      onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>API Base URL (optional)</Label>
                    <Input
                      placeholder="e.g. https://api.sendgrid.com"
                      value={formData.apiBaseUrl || ""}
                      onChange={(e) => setFormData({ ...formData, apiBaseUrl: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.isActive || false}
                onCheckedChange={(v) => setFormData({ ...formData, isActive: v })}
              />
              <Label>Set as Active Provider</Label>
            </div>
          </div>

          <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveProvider}>Save Provider</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteId !== null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {settings.providers?.find((p) => p.id === deleteId)?.name ?? "provider"}?</AlertDialogTitle>
            <AlertDialogDescription>
              The provider is removed from the list. Click “Save Email Settings” afterwards to apply the change.
              If it was active, the first remaining provider becomes active.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteId) deleteProvider(deleteId);
                setDeleteId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmailSettingsSection;
